import { UserDto } from '@issuemanager/api-client';
import {
  ContainerPopper,
  FakeDropdownBox,
  UserMultipleSelector,
} from '@issuemanager/apps/client/components';
import { Stack, useEventCallback } from '@mui/material';
import { ReactNode, useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

type TypeManagersSelectorProps = {
  value?: UserDto[];
  onChange: (value: UserDto[]) => void;
  label?: ReactNode;
};

export function TypeManagersSelector({
  value = [],
  onChange,
  label,
}: TypeManagersSelectorProps): JSX.Element {
  const { t } = useTranslation('settings');

  const handleChange = useEventCallback((newValue: UserDto[]) => {
    onChange(newValue);
  });

  const placeholderCounter = useMemo(() => {
    const placeholder =
      value.length > 0
        ? `(${value.length}) ${value.map((c) => c.name).join(', ')}`
        : t('issueTypes.form.issueType.placeholder.issueTypeManagers');
    return placeholder;
  }, [value, t]);

  return (
    <Stack>
      {label ?? null}
      <ContainerPopper
        renderTarget={({ handleClick, handleKeyDown, open }) => (
          <FakeDropdownBox
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role='button'
            aria-haspopup='listbox'
            aria-expanded={open}
            data-testid='issue-managers'
          >
            {placeholderCounter}
          </FakeDropdownBox>
        )}
        renderChildren={({ handleClose }) => (
          <UserMultipleSelector
            open
            value={value}
            onChange={handleChange}
            onClose={handleClose}
            TextFieldProps={{
              autoFocus: true,
            }}
            getTestIdOption={(option) => option.name}
          />
        )}
      />
    </Stack>
  );
}
